/**
 * Rally Data Utilities
 * Utilities for constructing CSV paths and deriving visualization data from rally API responses
 */

/**
 * CSV filename pattern for rally coordinates
 * Format: {videoName}_rally_coords.csv
 */
export const CSV_FILENAME_PATTERN = '{videoName}_rally_coords.csv';

/**
 * Builds the full CSV path from API response
 * @param {Object} rallyData - Response from rally API
 * @param {string} rallyData.videoName - Base video name (e.g., "IMG_0539" or "Prisha_0024_Near")
 * @param {string} rallyData.basePath - S3/base path prefix (e.g., "datasets/rally_detect/.../rallies/")
 * @returns {string} Full CSV path
 */
export const getCoordsCsvPath = (rallyData) => {
    if (!rallyData || !rallyData.videoName || !rallyData.basePath) {
        console.error('Invalid rally data:', rallyData);
        return null;
    }

    const { videoName, basePath } = rallyData;
    const csvFileName = `${videoName}_rally_coords.csv`;

    // Ensure basePath ends with '/'
    const normalizedBasePath = basePath.endsWith('/') ? basePath : `${basePath}/`;

    return `${normalizedBasePath}${csvFileName}`;
};

/**
 * Derives all visualization paths from rally data
 * Extracts CSV paths from the API response
 * 
 * @param {Object} rallyData - Response from rally API
 * @param {string} rallyData.player_coords_csv - S3 path to player coordinates CSV (for tracking & heatmap)
 * @param {string} rallyData.occupancy_csv - S3 path to occupancy CSV
 * @param {string} rallyData.videoName - Base video name (optional, legacy)
 * @param {string} rallyData.basePath - S3/base path prefix (optional, legacy)
 * @returns {Object} Data with extracted CSV paths
 */
export const deriveVisualizationPaths = (rallyData) => {
    if (!rallyData) {
        console.warn('[deriveVisualizationPaths] No rally data provided');
        return null;
    }

    // Modern API response format with direct CSV paths
    if (rallyData.player_coords_csv || rallyData.occupancy_csv) {
        console.log('[deriveVisualizationPaths] Extracting CSV paths from API response');

        return {
            ...rallyData,
            // Player tracking and heatmap use the same coords CSV
            playerTrackingCsvPath: rallyData.player_coords_csv,
            heatmapCsvPath: rallyData.player_coords_csv,
            // Occupancy has its own CSV
            occupancySourceCsvPath: rallyData.occupancy_csv,
        };
    }

    // Legacy format: construct paths from videoName and basePath
    if (rallyData.videoName && rallyData.basePath) {
        console.log('[deriveVisualizationPaths] Using legacy path construction');
        const coordsCsvPath = getCoordsCsvPath(rallyData);

        if (!coordsCsvPath) {
            console.warn('Failed to construct CSV path, returning original data');
            return rallyData;
        }

        return {
            ...rallyData,
            playerTrackingCsvPath: coordsCsvPath,
            heatmapCsvPath: coordsCsvPath,
            occupancySourceCsvPath: coordsCsvPath,
            summary: rallyData.summary || null,
            videoName: rallyData.videoName,
            basePath: rallyData.basePath
        };
    }

    // No CSV paths found - return data as-is (match statistics only)
    console.log('[deriveVisualizationPaths] Rally data does not contain CSV paths, using as match statistics');
    return rallyData;
};

/**
 * Extracts video filename from a full URL or path
 * @param {string} urlOrPath - Full URL or file path
 * @returns {string} Video filename
 */
export const extractVideoFileName = (urlOrPath) => {
    if (!urlOrPath) return null;

    try {
        // Handle URLs
        if (urlOrPath.includes('://')) {
            const url = new URL(urlOrPath);
            return url.pathname.split('/').pop();
        }

        // Handle file paths
        return urlOrPath.split('/').pop();
    } catch (err) {
        console.error('Failed to extract video filename:', err);
        return urlOrPath;
    }
};

/**
 * Extracts match ID from video filename by removing file extension
 * @param {string} filename - Video filename (e.g., "Prisha_0024_Near.mp4")
 * @returns {string} Match ID without extension (e.g., "Prisha_0024_Near")
 */
export const extractMatchIdFromFilename = (filename) => {
    if (!filename) return null;

    try {
        // Remove file extension (everything after the last dot)
        const lastDotIndex = filename.lastIndexOf('.');
        if (lastDotIndex > 0) {
            return filename.substring(0, lastDotIndex);
        }
        // If no extension found, return as is
        return filename;
    } catch (err) {
        console.error('Failed to extract match ID from filename:', err);
        return filename;
    }
};
